import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import ScanList from "../../components/common/ScanList";
import EditableScanDetail from "../../components/common/EditableScanDetail";
import EditableIssueList from "../../components/common/EditableIssueList";
import {
  useGetPlatformScansByWebsiteIdQuery,
  useGetPlatformScanByIdQuery,
  useUpdatePlatformScanMutation,
  // useDeletePlatformScanMutation,
  useCreatePlatformScanMutation,
} from "../../app/api/platform/platformScansApiSlice";
import {
  useGetPlatformIssuesByScanIdQuery,
  useUpsertPlatformIssueMutation,
  useDeletePlatformIssueMutation,
} from "../../app/api/platform/platformIssuesApiSlice";
import {
  useUpsertConsoleIssueMutation,
  useDeleteConsoleIssueMutation,
} from "../../app/api/console/consoleIssuesApiSlice";
import {
  useGetConsoleScanByIdQuery,
  useGetConsoleIssuesByScanIdQuery,
  useUpdateConsoleScanMutation,
  // useDeleteConsoleScanMutation,
  useCreateConsoleScanMutation,
} from "../../app/api/console/consoleScansApiSlice";
import {
  selectCurrentConsoleWebsiteId,
  useGetConsoleWebsiteByIdQuery,
  useGetScansByConsoleWebsiteIdQuery,
} from "../../app/api/console/consoleWebsitesApiSlice";
import {
  useGetFindingStatusesByScanIdQuery,
  useLazyGetFindingStatusesByScanIdQuery,
} from "../../app/api/console/platformFindingsApiSlice";
import { useGetPlatformActivityRunsQuery } from "../../app/api/platform/platformActivityApiSlice";
import { mergeActivityRunsIntoScans } from "../../components/common/mergeActivityRunsIntoScans";
import { hubActivityRunUrl } from "../admin/ControlTower/hubScanLinks";

// Constants for environment types
export const ENVIRONMENTS = {
  PRE_STAGING: "pre_staging",
  STAGING: "staging",
  PRODUCTION: "production",
};

function ScansByEnvironment({ environment }) {
  const [selectedScan, setSelectedScan] = useState(null);
  const [, setSelectedIssueIds] = useState([]);
  const [expandedScans, setExpandedScans] = useState({});
  console.log("expandedScans", expandedScans);

  const consoleWebsiteId = useSelector(selectCurrentConsoleWebsiteId);
  console.log("consoleWebsiteId", consoleWebsiteId);
  const { data: website, isLoading: isLoadingWebsite } =
    useGetConsoleWebsiteByIdQuery(consoleWebsiteId, {
      skip: !consoleWebsiteId,
    });

  const isPlatform = environment !== ENVIRONMENTS.PRE_STAGING;
  const useProd = environment === ENVIRONMENTS.PRODUCTION;
  // Backend env key for the V2 finding-status overlay (prod | staging).
  const platformEnv = useProd ? "prod" : "staging";

  const websiteId = isPlatform
    ? environment === ENVIRONMENTS.STAGING
      ? website?.staging_website_id
      : website?.prod_website_id
    : consoleWebsiteId;

  const stagingWebsiteId = website?.staging_website_id || null;

  console.log("stagingWebsiteId", stagingWebsiteId);
  const {
    data: consoleScans = [],
    isLoading: isLoadingConsoleScans,
    refetch: refetchConsoleScans,
  } = useGetScansByConsoleWebsiteIdQuery(consoleWebsiteId, {
    skip: !consoleWebsiteId || isPlatform,
  });

  const {
    data: platformScans = [],
    isLoading: isLoadingPlatformScans,
    refetch: refetchPlatformScans,
  } = useGetPlatformScansByWebsiteIdQuery(
    { websiteId, useProd },
    {
      skip: !websiteId || !isPlatform,
    }
  );

  // Hub Activity runs for the same website — fills in agentic runs that never
  // linked a V1 scan_id (shown on Hub, previously missing from Console Agents).
  const {
    data: activityRuns = [],
    isLoading: isLoadingActivityRuns,
  } = useGetPlatformActivityRunsQuery(
    { websiteId, useProd, pageSize: 100 },
    { skip: !websiteId || !isPlatform }
  );

  const { data: platformScanDetail, isLoading: isLoadingPlatformScanDetail } =
    useGetPlatformScanByIdQuery(
      { scanId: selectedScan?.id, useProd },
      { skip: !selectedScan?.id || !isPlatform || selectedScan?.activity_run_only }
    );

  const { data: consoleScanDetail, isLoading: isLoadingConsoleScanDetail } =
    useGetConsoleScanByIdQuery(selectedScan?.id, {
      skip: !selectedScan?.id || isPlatform,
    });

  const { data: platformScanIssues = [], isLoading: isLoadingPlatformIssues } =
    useGetPlatformIssuesByScanIdQuery(
      { scanId: selectedScan?.id, useProd },
      { skip: !selectedScan?.id || !isPlatform || selectedScan?.activity_run_only }
    );

  const { data: consoleScanIssues = [], isLoading: isLoadingConsoleIssues } =
    useGetConsoleIssuesByScanIdQuery(selectedScan?.id, {
      skip: !selectedScan?.id || isPlatform,
    });

  // V2 finding-status overlay for the selected scan (discards/closures live in
  // the platform findings table, not the V1 issue rows the console reads).
  const { data: findingStatusData } = useGetFindingStatusesByScanIdQuery(
    { scanId: selectedScan?.id, env: platformEnv },
    { skip: !selectedScan?.id || !isPlatform || selectedScan?.activity_run_only }
  );
  const [fetchFindingStatuses] = useLazyGetFindingStatusesByScanIdQuery();

  const [updatePlatformScan] = useUpdatePlatformScanMutation();
  const [createPlatformScan] = useCreatePlatformScanMutation();
  // const [deletePlatformScan] = useDeletePlatformScanMutation();
  const [updateConsoleScan] = useUpdateConsoleScanMutation();
  const [createConsoleScan] = useCreateConsoleScanMutation();
  // const [deleteConsoleScan] = useDeleteConsoleScanMutation();

  const [upsertPlatformIssue] = useUpsertPlatformIssueMutation();
  const [upsertConsoleIssue] = useUpsertConsoleIssueMutation();
  const [deleteConsoleIssue] = useDeleteConsoleIssueMutation();
  const [deletePlatformIssue] = useDeletePlatformIssueMutation();

  useEffect(() => {
    setSelectedScan(null);
    setSelectedIssueIds([]);
    setExpandedScans({});
  }, [consoleWebsiteId, websiteId, environment]);

  const processIssues = (issues, findingStatuses = {}) => {
    if (!issues || !Array.isArray(issues)) return [];

    if (environment === ENVIRONMENTS.PRE_STAGING) {
      return issues.map((issue) => ({
        ...issue.issue_content,
        id: issue.console_issue_id || issue.id,
        title: issue.issue_content?.title || "Untitled Issue",
      }));
    } else {
      return issues.map((issue) => {
        const issueId = issue.issue_id || issue.id;
        const title =
          issue.title ||
          issue.visible_issue_content?.title ||
          issue.created_issue_content?.title ||
          issue.issue_content?.title ||
          "Untitled Issue";

        // Overlay the V2 finding status (discarded/closed/open) when present —
        // V1 issue rows don't carry discards. Set it both at top level (read
        // first by the status-grouping list) AND inside visible_issue_content
        // (EditableIssue merges content with visible_issue_content winning, so
        // the top-level alone would be overridden back to "open").
        const overlay = findingStatuses?.[issueId];

        if (!overlay) {
          return { ...issue, id: issueId, title };
        }

        return {
          ...issue,
          id: issueId,
          title,
          status: overlay,
          visible_issue_content: {
            ...(issue.visible_issue_content || {}),
            status: overlay,
          },
        };
      });
    }
  };

  const handleScanExpand = async (scan) => {
    // Activity-run-only rows have no V1 scan to expand issues for.
    if (scan?.activity_run_only) {
      setExpandedScans((prev) => ({
        ...prev,
        [scan.id]: { loading: false, issues: [], activityRunOnly: true },
      }));
      return [];
    }

    try {
      setExpandedScans((prev) => ({
        ...prev,
        [scan.id]: { loading: true, issues: scan.issues || [] },
      }));

      let issuesData;
      let findingStatuses = {};
      if (environment === ENVIRONMENTS.PRE_STAGING) {
        const response = await fetch(`/scan/${scan.id}/issues`);
        issuesData = await response.json();
      } else {
        const [response, statusResult] = await Promise.all([
          fetch(`/api/v1/issues/scan/${scan.id}?use_prod=${useProd}`),
          fetchFindingStatuses({ scanId: scan.id, env: platformEnv })
            .unwrap()
            .catch(() => ({ statuses: {} })),
        ]);
        if (!response.ok) {
          throw new Error(`Error fetching issues: ${response.statusText}`);
        }
        issuesData = await response.json();
        findingStatuses = statusResult?.statuses || {};
      }

      const processedIssues = processIssues(issuesData, findingStatuses);

      setExpandedScans((prev) => ({
        ...prev,
        [scan.id]: { loading: false, issues: processedIssues },
      }));

      const updatedScan = { ...scan, issues: processedIssues };

      if (selectedScan?.id === scan.id) {
        setSelectedScan(updatedScan);
      }

      return processedIssues;
    } catch (error) {
      console.error("Failed to load issues for scan:", error);
      setExpandedScans((prev) => ({
        ...prev,
        [scan.id]: { loading: false, issues: [], error: true },
      }));
      return [];
    }
  };

  const handleScanSelect = (scan) => {
    const normalizedScan = {
      ...scan,
      id: scan.id || scan.scan_id || scan.console_scan_id,
      scan_id: scan.scan_id || scan.id || scan.console_scan_id,
      console_scan_id: scan.console_scan_id || scan.id || scan.scan_id,
      title: scan.title || "Untitled Scan",
    };

    setSelectedScan(normalizedScan);

    if (scan.activity_run_only) {
      return;
    }

    // If we already have issues for this scan, attach them
    if (expandedScans[normalizedScan.id]?.issues?.length > 0) {
      setSelectedScan({
        ...normalizedScan,
        issues: expandedScans[normalizedScan.id].issues,
      });
    }

    // Automatically expand this scan to load its issues if not already loaded
    if (!expandedScans[normalizedScan.id]) {
      handleScanExpand(normalizedScan);
    }
  };

  // Get appropriate scans based on environment.
  // Coerce null/undefined — RTK Query's `= []` default only covers undefined,
  // and a fulfilled null payload still crashes `.map` (ZENYT-CONSOLE-FRONTEND-PROD-4).
  const scans = (isPlatform ? platformScans : consoleScans) || [];

  // Process scans to add issues from expandedScans state, then union Hub
  // activity runs that lack a V1 scan so Console matches Hub Activity.
  const processedScans = mergeActivityRunsIntoScans(
    scans.map((scan) => {
      if (expandedScans[scan.id]?.issues) {
        return { ...scan, issues: expandedScans[scan.id].issues };
      }
      return scan;
    }),
    isPlatform ? activityRuns : []
  );

  // Get appropriate scan detail and issues based on environment
  const scanDetail = isPlatform ? platformScanDetail : consoleScanDetail;
  const scanIssues = isPlatform ? platformScanIssues : consoleScanIssues;

  // Process the scan issues for display, overlaying V2 finding statuses.
  const processedScanIssues = processIssues(
    scanIssues,
    findingStatusData?.statuses || {}
  );

  //! SCAN UPDATES
  const handleScanUpdate = async (updatedScan) => {
    try {
      const { environment: scanEnvironment, ...scanData } = updatedScan;

      const useProd = scanEnvironment === ENVIRONMENTS.PRODUCTION;

      if (isPlatform) {
        const scanId = scanData.id || scanData.scan_id;

        if (!scanId) {
          console.error(
            "Failed to update scan: No valid scan ID found",
            updatedScan
          );
          return;
        }

        const updatePayload = {
          title: scanData.title,
          excel_report_uri: scanData.excel_report_uri,
        };

        console.log("Update scan debug:", {
          scanId,
          websiteId,
          useProd,
          environment: scanEnvironment,
          updatePayload,
        });

        if (!websiteId) {
          console.error("Cannot update scan: websiteId is missing", {
            website,
            environment: scanEnvironment,
            isPlatform,
          });
          throw new Error("Website ID is required for platform scan updates");
        }

        await updatePlatformScan({
          scanId,
          scan: updatePayload,
          useProd,
          websiteId,
        }).unwrap();

        // Keep selectedScan in sync so the detail panel (and any consumers of
        // selectedScan) reflect the saved title without requiring a reload.
        if (selectedScan?.id === scanId || selectedScan?.scan_id === scanId) {
          setSelectedScan((prev) =>
            prev ? { ...prev, ...updatePayload } : prev
          );
        }

        refetchPlatformScans();
      } else {
        // For console API (pre-staging) - existing code for console updates
        const scanId = scanData.id || scanData.console_scan_id;

        if (!scanId) {
          console.error(
            "Failed to update scan: No valid scan ID found",
            updatedScan
          );
          return;
        }

        const updatePayload = {
          title: scanData.title,
        };

        await updateConsoleScan({
          scanId,
          scan: updatePayload,
        }).unwrap();

        if (selectedScan?.id === scanId || selectedScan?.console_scan_id === scanId) {
          setSelectedScan((prev) =>
            prev ? { ...prev, ...updatePayload } : prev
          );
        }

        refetchConsoleScans();
      }
    } catch (error) {
      console.error("Failed to update scan:", error);
      alert(`Update failed: ${error.message}`);
    }
  };

  //! ISSUE UPDATES
  const handleIssueUpdate = async (updatedIssue) => {
    try {
      const idField = isPlatform ? "issue_id" : "console_issue_id";
      const updatedIssues = processedScanIssues.map((issue) =>
        issue[idField] === updatedIssue[idField] ? updatedIssue : issue
      );
      if (isPlatform) {
        await upsertPlatformIssue({
          scanId: selectedScan.scan_id,
          issue: {
            issue_id: updatedIssue.id,
            scan_id: selectedScan.scan_id,
            issue_content: updatedIssue,
            // Include additional top-level fields
            tags: updatedIssue.tags,
            rank: updatedIssue.rank,
            discard_reason: updatedIssue.discard_reason,
          },
          useProd,
        }).unwrap();
        refetchPlatformScans();
      } else {
        await upsertConsoleIssue({
          scanId: selectedScan.scan_id,
          issue: {
            console_issue_id: updatedIssue.console_issue_id || updatedIssue.id,
            issue_content: {
              title: updatedIssue.title || "",
              description: updatedIssue.description || "",
              severity: updatedIssue.severity || "medium",
              status: updatedIssue.status || "open",
              regions: updatedIssue.regions || ["US"],
              platforms: updatedIssue.platforms || ["desktop"],
              issue_rows: updatedIssue.issue_rows || [],
              shortlink_url: updatedIssue.shortlink_url || "",
              shortlink_title: updatedIssue.shortlink_title || "",
            },
          },
        }).unwrap();

        // Update the expanded scans state for console
        if (selectedScan && expandedScans[selectedScan.id]) {
          setExpandedScans((prev) => ({
            ...prev,
            [selectedScan.id]: {
              ...prev[selectedScan.id],
              issues: updatedIssues,
            },
          }));
        }
      }
    } catch (error) {
      console.error("Failed to update issue:", error);
    }
  };

  // Delete an issue from the scan
  const handleIssueDelete = async (issueId) => {
    try {
      if (isPlatform) {
        // Call the platform API mutation
        await deletePlatformIssue({ issueId, useProd }).unwrap();
        refetchPlatformScans();
      } else {
        // Call the console API mutation
        await deleteConsoleIssue(issueId).unwrap(); // Pass object with issueId property
        refetchConsoleScans();
      }

      // Optimistically remove the deleted issue from local state
      if (selectedScan && expandedScans[selectedScan.id]) {
        setExpandedScans((prev) => ({
          ...prev,
          [selectedScan.id]: {
            ...prev[selectedScan.id],
            issues: prev[selectedScan.id].issues.filter((issue) =>
              isPlatform
                ? (issue.issue_id || issue.id) !== issueId
                : (issue.console_issue_id || issue.id) !== issueId
            ),
          },
        }));
      }
    } catch (error) {
      console.error("Failed to delete issue:", error);
    }
  };

  // Create a new scan
  const handleCreateScan = async () => {
    try {
      if (isPlatform) {
        if (!websiteId) {
          console.warn("Cannot create scan: website ID missing");
          return;
        }

        // Create a scan with required fields for platform API
        const newScan = await createPlatformScan({
          scan: {
            website_id: websiteId,
            scan_status: "to_review",
            title: `New Agent ${new Date().toLocaleString()}`,
            console_website_id: consoleWebsiteId, // Add required console_website_id
          },
          useProd,
        }).unwrap();

        await refetchPlatformScans();
        setSelectedScan(newScan);
        setExpandedScans((p) => ({
          ...p,
          [newScan.id]: { loading: false, issues: [] },
        }));
      } else {
        if (!consoleWebsiteId) {
          console.warn("Cannot create scan: console website ID missing");
          return;
        }

        // Create a scan for console environment - make sure to include all required fields
        const newScan = await createConsoleScan({
          console_website_id: consoleWebsiteId, // Use the correct field name for console API
          website_id: consoleWebsiteId, // Include website_id as well for compatibility
          scan_status: "to_review",
          title: `New Agent ${new Date().toLocaleString()}`,
        }).unwrap();

        await refetchConsoleScans();
        setSelectedScan(newScan);
        setExpandedScans((p) => ({
          ...p,
          [newScan.id]: { loading: false, issues: [] },
        }));
      }
    } catch (e) {
      console.error("Failed to create scan:", e);
      alert(`Failed to create scan: ${JSON.stringify(e.response || e)}`);
    }
  };

  // Delete multiple issues
  const handleDeleteMultipleIssues = async (scanId, issueIds) => {
    try {
      // If we have issues for this scan in expandedScans, filter them
      let updatedIssues = [];
      if (expandedScans[scanId]?.issues) {
        updatedIssues = expandedScans[scanId].issues.filter((issue) => {
          // Check both possible ID fields based on environment
          const id = isPlatform
            ? issue.issue_id || issue.id
            : issue.console_issue_id || issue.id;
          return !issueIds.includes(id);
        });
      }

      if (isPlatform) {
        // For platform, update the scan with filtered issues
        await updatePlatformScan({
          scanId,
          scan: {
            ...(scanId === selectedScan?.id ? scanDetail : {}),
            issues: updatedIssues,
          },
          useProd,
        }).unwrap();
        refetchPlatformScans();
      } else {
        // For console, just log deletion for now
        console.log("Deleted issues from scan:", scanId, issueIds);
      }

      // Update expandedScans state
      setExpandedScans((prev) => ({
        ...prev,
        [scanId]: {
          ...prev[scanId],
          issues: updatedIssues,
        },
      }));

      // If this is the selected scan, update scanIssues
      if (scanId === selectedScan?.id) {
        // Fetch updated issues for the selected scan
        let processedIssues = [];
        if (isPlatform) {
          const issuesResponse = await fetch(
            `/api/v1/issues/scan/${scanId}?use_prod=${useProd}`
          );
          const issuesData = await issuesResponse.json();
          processedIssues = processIssues(issuesData);
        } else {
          const response = await fetch(`/scan/${scanId}/issues`);
          const issuesData = await response.json();
          processedIssues = processIssues(issuesData);
        }

        // Update the selected scan with the new issues
        setSelectedScan((prev) => ({ ...prev, issues: processedIssues }));
      }
    } catch (error) {
      console.error("Failed to delete issues:", error);
    }
  };

  // Create a full scan detail with issues for the editor
  const fullScanDetail = selectedScan
    ? {
        ...selectedScan,
        ...(scanDetail || {}), // Merge with scanDetail if available
        issues: processedScanIssues || [],
      }
    : null;

  // Combine loading states based on environment
  const isLoading = isPlatform
    ? isLoadingWebsite || isLoadingPlatformScans || isLoadingActivityRuns
    : isLoadingConsoleScans;

  const isLoadingDetail = isPlatform
    ? isLoadingPlatformScanDetail || isLoadingPlatformIssues
    : isLoadingConsoleScanDetail || isLoadingConsoleIssues;

  // Get the appropriate title based on the environment
  const getPageTitle = () => {
    switch (environment) {
      case ENVIRONMENTS.PRE_STAGING:
        return "Pre-Staging Agents";
      case ENVIRONMENTS.STAGING:
        return "Staging Agents";
      case ENVIRONMENTS.PRODUCTION:
        return "Prod Agents";
      default:
        return "Agents";
    }
  };

  // Determine if the current website ID is valid based on environment
  const isWebsiteIdValid = isPlatform ? websiteId : consoleWebsiteId;

  // Message to display when no website is available
  const getNoWebsiteMessage = () => {
    if (!consoleWebsiteId) {
      return "Please select a website to view agents.";
    }

    if (isPlatform && !websiteId) {
      return environment === ENVIRONMENTS.STAGING
        ? "No staging website available for the selected website."
        : "No production website available for the selected website.";
    }

    return "Please select a website to view agents.";
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-h2 text-text-primary">{getPageTitle()}</h1>
        <p className="text-body-sm text-text-secondary mt-1">Manage agents and findings for {environment === ENVIRONMENTS.PRODUCTION ? "production" : "staging"}</p>
      </div>

      <div className="flex flex-col md:flex-row gap-6">
        {/* Left sidebar with scan list */}
        <div className="w-auto min-w-[330px] flex-shrink-0 md:max-w-xs">
          <ScanList
            scans={processedScans}
            onScanSelect={handleScanSelect}
            onScanExpand={handleScanExpand}
            selectedScan={selectedScan}
            onDeleteIssues={handleDeleteMultipleIssues}
            isLoading={isLoading}
            onCreateScan={handleCreateScan}
            stagingWebsiteId={stagingWebsiteId}
            productionWebsiteId={website?.prod_website_id}
            environment={environment}
          />
        </div>

        {/* Main content area */}
        <div className="flex-grow space-y-6">
          {isWebsiteIdValid ? (
            selectedScan?.activity_run_only ? (
              <div className="bg-white rounded-2xl border border-border-subtle p-6 space-y-3">
                <h2 className="text-h4 text-text-primary">{selectedScan.title}</h2>
                <p className="text-body-sm text-text-secondary">
                  This Hub activity run has no linked scan ID, so there are no
                  Console findings to edit or push. Open the run on Hub for the
                  full activity trace.
                </p>
                {selectedScan.activity_run_status && (
                  <p className="text-caption text-text-tertiary">
                    Status:{" "}
                    {selectedScan.activity_run_status === "canceled"
                      ? "Stopped (user canceled on Hub)"
                      : selectedScan.activity_run_status}
                    {typeof selectedScan.pass_count === "number"
                      ? ` · ${selectedScan.pass_count} pass`
                      : ""}
                  </p>
                )}
                <a
                  href={hubActivityRunUrl(
                    platformEnv,
                    selectedScan.activity_run_id || selectedScan.id
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-body-sm font-medium text-accent-purple hover:underline"
                >
                  Open run on Hub ↗
                </a>
              </div>
            ) : (
              <>
                {/* Scan Details */}
                <EditableScanDetail
                  scan={selectedScan ? fullScanDetail : null}
                  onScanUpdate={handleScanUpdate}
                  isLoading={isLoadingDetail}
                  environment={environment}
                  stagingWebsiteId={stagingWebsiteId}
                  productionWebsiteId={website?.prod_website_id}
                />

                {/* Issues List */}
                {selectedScan && (
                  <EditableIssueList
                    issues={processedScanIssues || []}
                    onUpdateIssue={handleIssueUpdate}
                    onDeleteIssue={handleIssueDelete}
                    isLoading={isLoadingDetail}
                    environment={environment}
                  />
                )}
              </>
            )
          ) : (
            <div className="bg-white rounded-2xl border border-border-subtle p-8 text-center">
              <p className="text-body-sm text-text-tertiary">{getNoWebsiteMessage()}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ScansByEnvironment;
